import {
  rAllFormState,
  rRelationFormChanges,
  rTranslations,
} from "app/utils/recoil";
import { colors, spacing } from "app/utils/theme";
import { debounce, get, startCase } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { Icon, Row, SearchBar, Spinner, Text } from ".";

import useDynamicText from "app/renderingApp/useDynamicText";
import useSpreadsheetRequests from "app/useSpreadsheetRequests";
import { getPixels, safeArray } from "app/utils/utils";
import styled from "styled-components";
import MuiDropdown from "./MuiDropdown";

const Container = styled.div`
  position: relative;
`;

const InnerContainer = styled.div`
  padding: 10px;
  flex-direction: column;
  display: flex;
`;

const Option = styled.div`
  font-size: 16px;
  padding: ${(p) => p.padding || "10px 12px 10px 12px"};
  cursor: pointer;
  border-top: ${(p) => (p.showBorder ? "1px" : "0px")} solid var(--grey1);
  display: flex;
  gap: ${spacing.s2};
  min-width: ${(p) => p.minWidth || "fit-content"};
  align-items: center;
  background: ${(p) => p.background};
  &:hover {
    background: ${(p) =>
      p.darkMode ? colors.darkModeInputBackground : "#f8fafb"};
  }
`;

const StyledContainer = styled.div`
  padding: ${(p) => p.padding || "10px"};
  border: ${(p) => p.border || `1px solid ${colors.pitchBorder}`};
  border-width: ${(p) => p.borderWidth};
  border-radius: ${(p) => getPixels(p.borderRadius || 8)};
  font-size: ${(p) => getPixels(p.fontSize)};
  font-weight: ${(p) => p.fontWeight};
  background: ${(p) => p.background};
  color: ${(p) => p.color};
  width: ${(p) => p.width};
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  height: 39px;
`;

const Autocomplete = ({ data }) => {
  const matchingRelation = get(data, "matchingRelation");

  const relationName = get(matchingRelation, "column2");
  const dataSourceId = get(data, "dataSourceId");
  const activeItemId = get(data, "activeItemId");

  const [relationFormChanges, setRelationFormChanges] =
    useRecoilState(rRelationFormChanges);

  const concatId = `${dataSourceId}__${activeItemId}`;

  const relationFormChange = get(relationFormChanges, concatId);

  const { getRecords, getRecord } = useSpreadsheetRequests();

  const { processDynamicText } = useDynamicText();

  const darkMode = get(data, "darkMode");

  const [fetching, setFetching] = useState(false);
  const [fetchingDisplayValue, setFetchingDisplayValue] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [rawOptions, setRawOptions] = useState([]);

  const [hasSearched, setHasSearched] = useState(false);
  const [valueLabel, setValueLabel] = useState(false);

  const [closeCounter, setCloseCounter] = useState(0);

  const value = get(data, "value");
  const displayValue = valueLabel || value;

  const translations = useRecoilValue(rTranslations);

  const selectText = get(translations, "selectText", "Select") || "Select";

  // Current form values for dynamic hidden filters
  const allFormState = useRecoilValue(rAllFormState);
  const formValues = useMemo(() => {
    const blockId = get(data, "blockId");
    return get(allFormState, blockId, {});
  }, [allFormState, data]);

  const triggerComponent = (
    <StyledContainer {...data}>
      {fetchingDisplayValue ? (
        <Spinner padding="0 0 0 0" size={18} />
      ) : (
        <>
          <Text
            // TODO - Translations
            data={{
              text: displayValue || `${selectText} ${startCase(relationName)}`,
            }}
          />

          <Row alignItems="center" gap="5px">
            {displayValue && (
              <Icon
                data={{
                  icon: "FiX",
                  onClick: (e) => {
                    e.stopPropagation();
                    data.onChange(null);
                    setValueLabel(null);
                    setRelationFormChanges({
                      ...relationFormChanges,
                      [concatId]: {
                        ...relationFormChange,
                        [relationName]: null,
                      },
                    });
                  },
                  size: 20,
                  color: "var(--grey7)",
                  hover: true,
                }}
              />
            )}
            <Icon data={{ icon: "FiChevronDown" }} />
          </Row>
        </>
      )}
    </StyledContainer>
  );

  const labelColumn = get(matchingRelation, "display_column");

  // Fetch options from the server
  const fetchOptions = async (v = null) => {
    setFetching(true);

    // Build hidden filters (processed with current form values)
    const hiddenFilters = safeArray(data, "hiddenFilters")
      .filter((f) => {
        const operator = get(f, "operator", "equals");
        const rawVal = get(f, "value");
        return (
          get(f, "key") &&
          (rawVal ||
            ["exists", "does_not_exist", "is_true", "is_false"].includes(
              operator
            ))
        );
      })
      .map((f) => ({
        ...f,
        value: processDynamicText({
          text: get(f, "value"),
          context: { form: formValues },
          skipGoogleSheetCell: true,
          skipCustomVariable: true,
        }),
      }));

    const searchFilter = v
      ? [
          {
            key: labelColumn,
            operator: "contains",
            value: v,
          },
        ]
      : [];

    const combinedFilters = [...hiddenFilters, ...searchFilter];

    getRecords({
      sheetId: matchingRelation.sheet1,
      filters: combinedFilters,
      pagination: { page_size: 100 },
      filtersConditionType: get(data, "filtersConditionType", "all"),
    }).then((sheetData) => {
      const records = get(sheetData, "data", []).map((r) => ({
        label: get(r, labelColumn),
        value: get(r, "frontly_id"),
      }));
      setRawOptions(get(sheetData, "data", []));
      setOptions(records);
      setFetching(false);
    });
  };

  // Handle the user searching for options
  const debouncedSearch = useCallback(
    debounce(async (v) => {
      if (v.length > 1) {
        if (matchingRelation && matchingRelation.sheet1) {
          setHasSearched(true);
          fetchOptions(v);
        }
      }
    }, 300), // Adjust the delay time as needed
    []
  );

  // Handle search
  useEffect(() => {
    if (search.length > 1) {
      debouncedSearch(search);
    }
  }, [search, debouncedSearch]);

  // Handle value
  useEffect(() => {
    if (!value) {
      setValueLabel(null);
      return;
    }

    const localMatch = options.find((option) => option.value === value);
    if (localMatch) {
      if (valueLabel !== localMatch.label) {
        setValueLabel(localMatch.label);
      }
      return;
    }

    if (!valueLabel && !fetchingDisplayValue) {
      setFetchingDisplayValue(true);
      getRecord({
        sheetId: matchingRelation.sheet1,
        recordId: value,
        rowIdColumn: "id",
      }).then((row) => {
        const newLabel = get(row, labelColumn);
        if (newLabel !== valueLabel) {
          setValueLabel(newLabel);
        }
        setFetchingDisplayValue(false);
      });
    }
  }, [value]);

  return (
    <Container>
      <MuiDropdown
        triggerComponent={triggerComponent}
        closeCounter={closeCounter}
        background={darkMode ? colors.darkModeInputBackground : "white"}
        onOpen={() => {
          // If no options, fetch some
          if (options.length === 0) {
            fetchOptions(null);
          }
        }}
      >
        <InnerContainer>
          <SearchBar
            data={{
              value: search,
              placeholder: "Search records",
              onChange: (v) => setSearch(v),
            }}
          />
          {fetching && <Spinner padding="20px 0 10px 5px" />}

          {options.length === 0 && hasSearched && !fetching && (
            <Text
              data={{ text: "No results match this search", margin: "10px" }}
            />
          )}

          {options.length === 0 && !hasSearched && !fetching && (
            <Text data={{ text: "Begin typing to search", margin: "10px" }} />
          )}

          {!fetching &&
            options.map((option, i) => (
              <Option
                id={`simpleDropdown-option-${i}`}
                key={i}
                darkMode={darkMode}
                background={
                  darkMode
                    ? colors.darkModeLightBackground
                    : option.active && "#eceef2"
                }
                padding={data.optionPadding}
                minWidth={data.optionMinWidth}
                onClick={() => {
                  data.onChange(option.value);

                  const match = rawOptions.find(
                    (o) => o.frontly_id === option.value
                  );

                  setRelationFormChanges({
                    ...relationFormChanges,
                    [concatId]: {
                      ...relationFormChange,
                      [relationName]: match,
                    },
                  });

                  setCloseCounter(closeCounter + 1);
                }}
              >
                {option.label}
              </Option>
            ))}
        </InnerContainer>
      </MuiDropdown>
    </Container>
  );
};

export default Autocomplete;
